import React, { useRef, useState } from "react";
import { Move, ZoomIn, ImageIcon, RotateCw, RotateCcw, Images } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import ImageLibrary from "../pages/admin/ImageLibrary";

// Admin control: pick an image (library or URL), drag the focal point to choose
// which part shows, zoom, and rotate.
export default function ImageEditor({ label, value, onChange, testid = "image-editor" }) {
  const img = { url: "", pos_x: 50, pos_y: 50, zoom: 1, rotate: 0, ...(value || {}) };
  const boxRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [libOpen, setLibOpen] = useState(false);

  const setFocal = (clientX, clientY) => {
    const box = boxRef.current?.getBoundingClientRect();
    if (!box) return;
    const x = Math.min(100, Math.max(0, ((clientX - box.left) / box.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - box.top) / box.height) * 100));
    onChange({ ...img, pos_x: Math.round(x), pos_y: Math.round(y) });
  };

  const rotateBy = (delta) => {
    let r = (Math.round(img.rotate) + delta) % 360;
    if (r > 180) r -= 360;
    if (r < -180) r += 360;
    onChange({ ...img, rotate: r });
  };

  return (
    <div data-testid={testid} className="rounded-2xl border border-primary/10 bg-card p-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <ImageIcon size={16} className="text-secondary" />
          <h3 className="font-semibold text-primary">{label}</h3>
        </div>
        <button type="button" onClick={() => setLibOpen(true)} data-testid={`${testid}-library-btn`}
          className="flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1.5 text-xs font-semibold hover:bg-primary/20 transition-colors">
          <Images size={14} /> Choose / Upload
        </button>
      </div>

      <div
        ref={boxRef}
        data-testid={`${testid}-canvas`}
        className="relative w-full aspect-[16/9] rounded-xl overflow-hidden bg-muted cursor-crosshair select-none"
        onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); setDragging(true); setFocal(e.clientX, e.clientY); }}
        onPointerMove={(e) => { if (dragging) setFocal(e.clientX, e.clientY); }}
        onPointerUp={() => setDragging(false)}
        onPointerLeave={() => setDragging(false)}
      >
        {img.url ? (
          <img src={img.url} alt="" className="w-full h-full object-cover pointer-events-none"
            style={{ objectPosition: `${img.pos_x}% ${img.pos_y}%`, transform: `scale(${img.zoom}) rotate(${img.rotate}deg)`, transformOrigin: `${img.pos_x}% ${img.pos_y}%` }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">Choose an image or paste a URL below</div>
        )}
        {img.url && (
          <div data-testid={`${testid}-focal`} className="absolute w-7 h-7 -ml-3.5 -mt-3.5 rounded-full border-2 border-white bg-secondary/70 shadow-lg pointer-events-none flex items-center justify-center"
            style={{ left: `${img.pos_x}%`, top: `${img.pos_y}%` }}>
            <Move size={13} className="text-white" />
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Move size={12} /> Drag on the image to set the focal point ({img.pos_x}%, {img.pos_y}%)</p>

      <div className="mt-3">
        <Label className="flex items-center gap-1.5"><ZoomIn size={14} /> Zoom ({img.zoom.toFixed(2)}x)</Label>
        <input type="range" min="1" max="2" step="0.05" value={img.zoom} data-testid={`${testid}-zoom`}
          onChange={(e) => onChange({ ...img, zoom: parseFloat(e.target.value) })}
          className="w-full mt-1.5 accent-secondary" />
      </div>

      <div className="mt-3">
        <Label className="flex items-center gap-1.5"><RotateCw size={14} /> Rotate ({img.rotate}°)</Label>
        <div className="flex items-center gap-2 mt-1.5">
          <button type="button" onClick={() => rotateBy(-90)} data-testid={`${testid}-rotate-left`}
            className="rounded-full bg-primary/10 text-primary w-8 h-8 flex items-center justify-center hover:bg-primary/20"><RotateCcw size={15} /></button>
          <input type="range" min="-180" max="180" step="1" value={img.rotate} data-testid={`${testid}-rotate`}
            onChange={(e) => onChange({ ...img, rotate: parseInt(e.target.value, 10) })}
            className="flex-1 accent-secondary" />
          <button type="button" onClick={() => rotateBy(90)} data-testid={`${testid}-rotate-right`}
            className="rounded-full bg-primary/10 text-primary w-8 h-8 flex items-center justify-center hover:bg-primary/20"><RotateCw size={15} /></button>
        </div>
      </div>

      <div className="mt-3">
        <Label>Image URL</Label>
        <Input value={img.url} data-testid={`${testid}-url`} placeholder="https://… (or use Choose / Upload)"
          onChange={(e) => onChange({ ...img, url: e.target.value })} className="mt-1.5" />
      </div>

      <ImageLibrary open={libOpen} onClose={() => setLibOpen(false)} currentUrl={img.url}
        onSelect={(url) => { onChange({ ...img, url }); setLibOpen(false); }} />
    </div>
  );
}
